import React, { Component, ErrorInfo, ReactNode } from 'react';
import { chunkFailureUrl, loadedEntryVersion, reloadChunkOnce } from '../lib/chunkRecovery';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorCount: number;
  recoveryMessage: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
    errorCount: 0,
    recoveryMessage: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[FRONTEND ERROR BOUNDARY]', error, errorInfo);
    
    this.setState((prev) => ({ errorCount: prev.errorCount + 1 }));

    // Auto-recovery: se o erro é removeChild (conhecido do motion/react),
    // tenta auto-recuperar sem recarregar a página (até 3 vezes)
    if (error.name === 'NotFoundError' && error.message.includes('removeChild')) {
      if (this.state.errorCount < 3) {
        setTimeout(() => {
          this.setState({ hasError: false, error: null });
        }, 500);
      }
    }
  }

  private handleReset = () => {
    const failedUrl = chunkFailureUrl(this.state.error);
    if (failedUrl) {
      const recoveryMessage = reloadChunkOnce(failedUrl, loadedEntryVersion(), {
        online: () => navigator.onLine !== false,
        confirm: (message) => window.confirm(message),
        storage: () => window.sessionStorage,
        reload: () => window.location.reload(),
      });
      if (recoveryMessage) this.setState({ recoveryMessage });
      return;
    }
    if (window.confirm('Recarregar pode descartar alterações não salvas. Deseja continuar?')) window.location.reload();
  };

  private handleSoftReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public override render() {
    if (this.state.hasError) {
      const isRemoveChildError = this.state.error?.name === 'NotFoundError' 
        && this.state.error?.message.includes('removeChild');
      
      const isChunkError = Boolean(chunkFailureUrl(this.state.error));
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#111B21] flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-6 text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 flex items-center justify-center mx-auto">
              <AlertTriangle size={24} />
            </div>

            <div className="space-y-1">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {isChunkError ? 'Não foi possível carregar este módulo' : 'Ocorreu um erro inesperado'}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isChunkError ? 'A conexão ou uma troca de versão pode ter interrompido o carregamento. Recarregar é opcional e pode perder rascunhos não salvos.' : 'Identificamos um problema na exibição da interface.'}
              </p>
            </div>

            {this.state.error?.message && (
              <div className="p-3 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/50 rounded-xl text-left">
                <p className="text-[11px] font-mono text-rose-700 dark:text-rose-300 break-words leading-relaxed">
                  {this.state.error.message}
                </p>
              </div>
            )}

            {this.state.recoveryMessage && <p role="status" className="text-sm text-amber-700">{this.state.recoveryMessage}</p>}
            <div className="flex items-center justify-center gap-3 pt-2">
              {isRemoveChildError && (
                <button
                  onClick={this.handleSoftReset}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 font-bold text-xs rounded-xl shadow-sm transition-all cursor-pointer"
                >
                  <RefreshCw size={14} />
                  Tentar novamente
                </button>
              )}
              
              <button
                onClick={this.handleReset}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--tenant-primary,#2563eb)] hover:opacity-90 text-white font-bold text-xs rounded-xl shadow-sm transition-all cursor-pointer"
              >
                <RefreshCw size={14} />
                Recarregar Página
              </button>

              <a
                href="/"
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold text-xs rounded-xl transition-all"
              >
                <Home size={14} />
                Início
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}


